import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.id === article.category) : null;
  const relatedArticles = article
    ? articles.filter((a) => article.relatedIds?.includes(a.id)).slice(0, 3)
    : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 51扒瓜网`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center px-4">
          <h1 className="font-heading text-2xl text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:underline">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <nav aria-label="面包屑导航" className="pt-24 pb-4 px-4 md:px-8 lg:px-12">
        <ol className="flex items-center gap-2 text-sm text-foreground-500 max-w-4xl mx-auto">
          <li>
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          </li>
          <li aria-hidden="true"><i className="ri-arrow-right-s-line text-xs" /></li>
          {category && (
            <>
              <li>
                <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors">
                  {category.name}
                </Link>
              </li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line text-xs" /></li>
            </>
          )}
          <li aria-current="page" className="text-primary-600 font-medium line-clamp-1">{article.title.slice(0, 30)}...</li>
        </ol>
      </nav>

      {/* Article Header */}
      <header className="px-4 md:px-8 lg:px-12 pb-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Link
              to={`/category/${category?.slug || ''}`}
              className="px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
            >
              {article.categoryName}
            </Link>
            {article.isExclusive && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-50 text-accent-700">
                独家爆料
              </span>
            )}
            {article.isHot && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                热门
              </span>
            )}
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl md:text-4xl text-foreground-950 leading-tight mb-5">
            {article.title}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
            {article.summary}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/70">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {(article.views / 10000).toFixed(1)}万阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-heart-line" />
              {article.likes.toLocaleString()}点赞
            </span>
          </div>
        </div>
      </header>

      {/* Article Image */}
      <div className="px-4 md:px-8 lg:px-12 mb-8">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-56 sm:h-72 md:h-80 object-cover object-top"
            />
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="px-4 md:px-8 lg:px-12 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            {article.content.split('\n\n').map((paragraph, i) => {
              if (paragraph.startsWith('#')) {
                const level = paragraph.match(/^#+/)?.[0].length || 1;
                const text = paragraph.replace(/^#+\s*/, '');
                const Tag = `h${Math.min(level + 1, 6)}` as keyof JSX.IntrinsicElements;
                return (
                  <Tag key={i} className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mt-8 mb-4">
                    {text}
                  </Tag>
                );
              }
              return (
                <p key={i} className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
                  {paragraph}
                </p>
              );
            })}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
            <span className="text-sm text-foreground-500 mr-2">相关标签：</span>
            {article.categoryName && (
              <span className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-800">{article.categoryName}</span>
            )}
            {article.isExclusive && (
              <span className="px-3 py-1 rounded-full text-xs bg-accent-100 text-accent-800">独家爆料</span>
            )}
            {article.isHot && (
              <span className="px-3 py-1 rounded-full text-xs bg-primary-100 text-primary-800">热门话题</span>
            )}
          </div>

          {/* Share */}
          <div className="flex items-center gap-3 mt-6">
            <span className="text-sm text-foreground-500">分享到：</span>
            <button type="button" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors">
              <i className="ri-weibo-line" />
            </button>
            <button type="button" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors">
              <i className="ri-wechat-line" />
            </button>
            <button type="button" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors">
              <i className="ri-link" />
            </button>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="px-4 md:px-8 lg:px-12 py-12 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="space-y-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.slug}`}
                  className="group flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/60 transition-all"
                >
                  <div className="w-24 h-16 md:w-32 md:h-20 rounded-lg overflow-hidden shrink-0">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 leading-snug mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 line-clamp-1">
                      {related.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: '51扒瓜网的内容来源可靠吗？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '51扒瓜网所有爆料内容均经过多方核实，我们与行业内资深人士保持密切联系，确保信息的真实性和时效性。每篇独家爆料都附有详细的证据链和知情人士陈述。',
              },
            },
            {
              '@type': 'Question',
              name: '51扒瓜网多久更新一次内容？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '51扒瓜网每日早8点至凌晨2点持续更新，平均每日发布15-20条最新娱乐八卦和网络热点资讯，确保瓜友们第一时间获取最新猛料。',
              },
            },
          ],
        })}
      </script>

      {/* Article Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          image: article.image,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '51扒瓜网',
            logo: {
              '@type': 'ImageObject',
              url: 'http://rkldzswyxgs.com/logo.png',
            },
          },
          datePublished: article.date,
          dateModified: article.date,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `http://rkldzswyxgs.com/article/${article.slug}`,
          },
        })}
      </script>

      {/* BreadcrumbList Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'http://rkldzswyxgs.com/',
            },
            ...(category
              ? [
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: category.name,
                    item: `http://rkldzswyxgs.com/category/${category.slug}`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: article.title,
                    item: `http://rkldzswyxgs.com/article/${article.slug}`,
                  },
                ]
              : [
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: article.title,
                    item: `http://rkldzswyxgs.com/article/${article.slug}`,
                  },
                ]),
          ],
        })}
      </script>

      <Footer />
    </div>
  );
}